import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

// ONE MARKETPLACE SETTING'S INPUT, by its DATATYPE — a SELECT from AVAILABLEVALUES ("Label:value" pairs, comma or pipe
// separated; a bare entry is its own value), a PASSWORD that is never autofilled, a NUMBER, or text. Shared by the
// credential add form (CredentialModal) and a pipeline source's Config (ImplementationBoard), so a setting is drawn
// the same way wherever its value is asked for.
//
// `def` takes either the marketplacesettings column names (DATATYPE, AVAILABLEVALUES, PLACEHOLDER, SETTINGNAME) or
// their camelCase forms (dataType, availableValues, placeholder, name).
export const settingOptions = (availableValues) => String(availableValues || '')
    .split(/[,|]/)
    .map(v => {
        const [lbl, val] = v.trim().split(':');
        return { label: (lbl || '').trim(), value: (val ?? lbl ?? '').trim() };
    })
    .filter(o => o.label);

export default function MarketplaceSettingField({ def, value, onChange, className = 'ppw-input', id, disabled, placeholder }) {
    const dataType = String(def.DATATYPE || def.dataType || '').toUpperCase();
    const name = def.SETTINGNAME || def.name;
    const hint = placeholder !== undefined ? placeholder : (def.PLACEHOLDER || def.placeholder || name);
    // A PASSWORD field can be revealed with the eye. The value is already loaded in the field for editing, so this is
    // a show/hide of what is in hand, not a new fetch — convenience, not a fresh exposure.
    const [reveal, setReveal] = useState(false);
    if (dataType === 'SELECT') {
        const opts = settingOptions(def.AVAILABLEVALUES || def.availableValues);
        // A SELECT shows its default the way a text field shows one in its placeholder: when the marketplace has an
        // inheritable default and the field is left empty, the empty option names it (by its friendly label), so
        // "empty uses the system's default" is visible rather than a bare "— Select —".
        const dflt = def.defaultValue != null && def.defaultValue !== '' ? String(def.defaultValue) : null;
        const dfltLabel = dflt ? ((opts.find(o => o.value === dflt) || {}).label || dflt) : null;
        // With no value of its own, a SELECT shows its marketplace default SELECTED (not a blank "— Select —"), so the
        // effective value — what the system uses when the field is left empty — is what the box reads. Leaving it as
        // the default saves nothing (it just inherits); only picking a different option stores an override.
        return (
            <select id={id} className={className} value={value || dflt || ''} disabled={disabled} onChange={e => onChange(e.target.value)}>
                <option value="">{dfltLabel ? `— Default: ${dfltLabel} —` : '— Select —'}</option>
                {opts.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
        );
    }
    const isPassword = dataType === 'PASSWORD';
    if (isPassword) {
        return (
            <div style={{ position: 'relative' }}>
                <input
                    id={id}
                    className={className}
                    type={reveal ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={value || ''}
                    disabled={disabled}
                    onChange={e => onChange(e.target.value)}
                    placeholder={hint}
                    style={{ paddingRight: 40 }}
                />
                <button
                    type="button"
                    onClick={() => setReveal(r => !r)}
                    aria-label={reveal ? 'Hide value' : 'Show value'}
                    title={reveal ? 'Hide value' : 'Show value'}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6b7280', display: 'flex' }}
                >
                    {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
        );
    }
    return (
        <input
            id={id}
            className={className}
            type={dataType === 'NUMBER' ? 'number' : 'text'}
            autoComplete="off"
            value={value || ''}
            disabled={disabled}
            onChange={e => onChange(e.target.value)}
            placeholder={hint}
        />
    );
}
